// The two lines Exitium sells, and the words for the websites line.
//
// Every store on the board is sold one of two ways: the furniture platform
// (the plan's own words in salesPlan.js) or a website for a restaurant, food
// truck or local business. The steps, the outcomes and where each outcome
// sends the store are the same for both; only the words change. This file
// holds the words for the websites line and nothing that depends on
// salesPlan.js, so salesPlan can lay them over the plan itself. That way
// the Arena, the card, the drawer, the Flow guide and the stored
// next_action label all say the same thing for a restaurant.
//
// Phillip, 2026-11-03: Ibrahim, Umar and Abdullah all sell to restaurants;
// Phillip keeps furniture.

export const NICHES = {
  websites: { key: 'websites', label: 'Websites', proof: 'Drizzled, Stockton: 75% more online orders', accent: '#E8A0B8' },
  furniture: { key: 'furniture', label: 'Furniture platform', proof: 'East Bay Furniture runs it', accent: '#6B8F5E' },
};

export const SITE = 'drizzled209.com';
export const ALL = 'exitiumlabs.com/websites';
// The offer, as the site says it (src/site/faqs.js, "What does it cost?").
export const WEBSITE_PRICE = '$197 a month';
export const WEBSITE_SETUP = 'the $500 setup waived on a one-year contract';

// Which line a store is sold. A tag of 'websites' or 'furniture' on the row
// wins; then the lead-list area; then a food word in the name. The restaurant
// lists (sql/sales-pipeline 11, 15, 17) carry the 'websites' tag.
const TEMP_KEYS = new Set(['hot', 'warm', 'cold']);
const TIER_RE = /^tier:([ABC])$/i;
const DNC = 'do not contact';
const areaTag = (tags) => tags.find((t) => !TEMP_KEYS.has(t) && !TIER_RE.test(t) && t !== DNC) || '';
const WEBSITE_AREA = /^(stockton|lodi|manteca|tracy|modesto)\b/i;
const FOOD = /\b(taco|tacos|taqueria|restaurant|food truck|trailer|pizza|pizzeria|cafe|café|bakery|panaderia|pho|burger|burgers|bbq|barbecue|sushi|boba|deli|diner|mariscos|birria|donut|donuts|coffee|tea house|grill|cocina|antojitos|tortas|wings|ramen|thai|kabob|kebab|teriyaki|catering)\b/i;
export const nicheOf = (p) => {
  const tags = Array.isArray(p?.tags) ? p.tags : [];
  if (tags.includes('websites')) return 'websites';
  if (tags.includes('furniture')) return 'furniture';
  if (WEBSITE_AREA.test(areaTag(tags))) return 'websites';
  if (FOOD.test(String(p?.name || ''))) return 'websites';
  return 'furniture';
};

// The websites line, step by step, over the same outcomes as the plan. Keys
// match the plan's action keys (CADENCE, DEMO_STEPS, `${stage}_action`,
// 'reply'). `outcomes` here is only a label per outcome key: what the button
// says. Where the store goes when it is pressed never changes.
export const WEBSITE_SCRIPTS = {
  call1: {
    label: 'Call the owner',
    goal: 'Thirty seconds. Name Drizzled, name the number, say the link is on its way. The call is what makes the email get opened. Most will not pick up, that is fine.',
    say: 'Hi, is this [owner]? This is [rep] with Exitium. We built the website for Drizzled, the dessert trailer in Stockton, and their online orders went up 75 percent. I am sending you their site right now so you can see it on your phone. Ten seconds, then I will let you get back to it.',
    voicemail: `Hi [owner], [rep] with Exitium. We built Drizzled's website in Stockton and their online orders went up 75 percent. I am sending you the site right now, ${SITE}. My number is [phone].`,
    outcomes: { spoke: 'Spoke. Sending the Drizzled site', booked: 'Booked a time to show their site' },
  },
  video: {
    label: 'Email the Drizzled site',
    short: 'Email',
    goal: 'Within the hour. One link they can tap, one line about them, one ask.',
    say: `Subject: The site we built for Drizzled in Stockton\n\n[owner], this is [rep] with Exitium. I just left you a message.\n\nHere is the site: https://${SITE}\n\nMenu with photos, an order button on every screen, and a page for every dish so a search for it lands on them. Their online orders went up 75 percent after it went live.\n\nI would like to show you what [store]'s would look like. Thirty minutes, on your phone, whenever you are not slammed.\n\nEvery site we have built is at https://${ALL}\n\n[rep]\n[phone]`,
  },
  dm: {
    label: 'Message them on Instagram or Facebook',
    goal: 'Food owners live on Instagram. Same link, two lines.',
    say: `Hi [owner], [rep] with Exitium. We built Drizzled's site (${SITE}) and their online orders went up 75 percent. I would love to show you [store]'s version. Thirty minutes this week?`,
  },
  visit: {
    label: 'Walk in between the rushes',
    goal: 'Two to four in the afternoon. Order something. Show Drizzled on your phone. Ask who decides.',
    say: `Hi, I am [rep] with Exitium. I am the one who sent the Drizzled site. Is [owner] in? I would love ten minutes to show what [store]'s would look like on a phone.`,
    outcomes: { showed: 'Showed it on their phone', booked: 'Booked a time to come back' },
  },
  text: {
    label: 'Text the link',
    goal: 'Only if they gave you a cell or texted you first. Never cold text a number off the list. The link they can tap, nothing else.',
    say: `Hi [owner], [rep] with Exitium. This is the site we built for Drizzled in Stockton: https://${SITE}. Theirs went to 75 percent more online orders. Can I show you [store]'s in ten minutes this week?`,
  },
  call2: {
    label: 'Second call',
    goal: 'Did you get to look at it? Then the one question: what do people land on when they search you tonight?',
    say: 'Hi [owner], [rep] with Exitium again. Did you get a chance to open the Drizzled site? Quick question: when someone searches [store] tonight, what do they land on, and can they order from it? That is the whole thing we fix.',
    voicemail: 'Hi [owner], [rep] with Exitium. Following up on the Drizzled site I sent. One question when you have a second: when someone searches you tonight, can they order from what they find? [phone].',
    outcomes: { pilot: 'Yes. Build the site', booked: 'Booked a time to show it' },
  },
  email2: {
    label: 'One question email',
    goal: 'Short. The outcome, not the site. Then the store parks and comes back by itself.',
    say: `Subject: One question\n\n[owner], when someone in [city] searches for what [store] sells tonight, do they land on you, and can they order in two taps?\n\nThat is what we built for Drizzled: https://${SITE}. 75 percent more online orders after.\n\nWorth thirty minutes?\n\n[rep]\n[phone]`,
    outcomes: { pilot: 'Yes. Build the site' },
  },
  demo_confirm: {
    label: 'Confirm the demo',
    goal: 'The day before. One text with the time. Before you go, load their menu into the preview so what they see is their site, not ours.',
    say: `Hi [owner], [rep] with Exitium. Confirming [time] to show you [store]'s site on your phone. I will bring the menu already loaded. See you then.`,
  },
  demo_run: {
    label: 'Run the demo',
    goal: "Their menu, on their phone, in their hands. Then Drizzled's orders before and after. Then ask for the yes right there.",
    say: 'This is Drizzled, same size as you. Here is the order button, here is a dish page, here is what Google shows for it. Now here is [store]: your menu and your order button, on your phone. Same thing, your name on it.',
    outcomes: { pilot: 'Yes, right there. Build it', showed: 'Showed it. They want to think' },
  },
  demo_ask: {
    label: 'Ask for the yes',
    short: 'Ask',
    goal: `Within two days of the demo. ${WEBSITE_PRICE}, ${WEBSITE_SETUP}. The build starts the day they say yes.`,
    say: `You saw it on your own phone. It is ${WEBSITE_PRICE}: the menu, online ordering, and every page written so Google and the AI assistants find you. The $500 setup is waived on a year. Your domain, your old links keep working, and changes are one message to us. Shall I start the build today?`,
    voicemail: `Hi [owner], [rep] with Exitium. Following up on [store]'s site. ${WEBSITE_PRICE}, setup waived on a year, and I can start the build this week. [phone].`,
    outcomes: { pilot: 'Yes. Build the site', another_demo: 'Wants another look. Book it' },
  },
  pilot_action: {
    label: 'Build check-in',
    short: 'Build',
    goal: 'Until the site is live: the menu, the photos, the hours and the domain login. One message a week. The day it is live, they are a customer.',
    say: `Quick check on [store]'s site. I have the menu; I still need the photos, the hours and the domain login, and we go live as soon as that is in. Also, who are two owners you know who should see this?`,
    outcomes: { converted: 'Site is live. Customer', checked: 'Checked in, build on track', cancelled: 'Backed out before launch' },
  },
  customer_action: {
    label: 'Ask for two referrals',
    goal: `Site live, ${WEBSITE_PRICE}. Ask every customer, every month. Take the menu, hours or photo changes they want while you have them.`,
    say: 'Who are two owners you know who need a site like yours? I will mention your name. And anything on the menu or hours you want changed?',
  },
  parked_action: {
    say: "Hi [owner], [rep] with Exitium. We talked a while back about a site like Drizzled's. A few more places near you have one now. Worth ten minutes?",
  },
  reply: {
    say: `[owner], thanks for getting back to me.\n\nThirty minutes on your phone is the fastest way to see [store]'s site with your own menu on it. Does this week work?\n\n[rep]\n[phone]`,
    outcomes: { pilot: 'Yes. Build the site', booked: 'They want to talk. Book it' },
  },
};

// What the stages mean on the websites line, where they differ. Pilot is
// the build: they said yes, the site is being made, and it goes live within
// days. Customer is the site live at the monthly price.
export const STAGE_WORDS = {
  websites: {
    demo: { hint: 'Saw their site on their phone, or booked a time to.' },
    pilot: { label: 'Building', hint: 'Said yes. Menu, photos and domain in, live within days.' },
    customer: { hint: `${WEBSITE_PRICE}. Ask for two referrals.` },
  },
};

/** The plan's action with the websites words laid over it when that is the line. Outcome keys, and where each goes, never change. */
export function withLine(action, niche) {
  if (!action || niche !== 'websites') return action;
  const o = WEBSITE_SCRIPTS[action.key];
  if (!o) return action;
  const { outcomes: labels, ...words } = o;
  const outcomes = labels && Array.isArray(action.outcomes) ? action.outcomes.map((x) => (labels[x.key] ? { ...x, label: labels[x.key] } : x)) : action.outcomes;
  return { ...action, ...words, outcomes };
}

/** The label a step carries on this line (what gets stored in next_action). */
export const lineLabel = (step, key, niche) => (niche === 'websites' && WEBSITE_SCRIPTS[key]?.label) || step?.label || null;
